import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Clock, Camera } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0A0B0E] px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

const empty = { about: "", description: "", email: "", website: "", address: "", vertical: "" };

/** Display photo, About line and green-tick standing of the business WhatsApp number. */
export function WhatsAppProfile({ metaStatus }) {
  const [form, setForm] = useState(empty);
  const [photo, setPhoto] = useState("");
  const [standing, setStanding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/whatsapp/profile").then(({ data }) => {
      const p = data.profile || {};
      setForm({
        about: p.about || "", description: p.description || "", email: p.email || "",
        website: p.website || "", address: p.address || "", vertical: p.vertical || "",
      });
      setPhoto(p.photo_url || "");
      setStanding(data.standing || null);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (metaStatus?.configured && open) load(); }, [metaStatus, open, load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/whatsapp/profile", form);
      toast.success("Saved — this is what customers see on your WhatsApp profile");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo is bigger than 5 MB — use a smaller one");
    setBusy(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post("/admin/whatsapp/profile/photo", data);
      setPhoto(res.data.photo_url || "");
      toast.success("Display photo updated on WhatsApp");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!metaStatus?.configured) return null;

  const badge = standing?.official_badge;
  const nameOk = standing?.name_status === "APPROVED";

  return (
    <div className="rounded-2xl border border-white/10 bg-[#12141A] p-5" data-testid="wa-profile-box">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-display text-lg font-bold">Your WhatsApp profile — photo, name & green tick</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="wa-profile-number">
            {standing?.number || metaStatus?.number || "Business number"}
          </p>
        </div>
        <button
          data-testid="wa-profile-toggle"
          onClick={() => setOpen(!open)}
          className="rounded-full border border-white/10 px-4 py-2 text-xs text-slate-300 hover:bg-white/5"
        >
          {open ? "Hide" : "Open profile settings"}
        </button>
      </div>

      {open && (
        <div className="mt-5 space-y-4">
          {standing?.error && (
            <p className="rounded-xl border border-red-400/20 bg-red-400/5 p-3 text-xs leading-relaxed text-red-300" data-testid="wa-profile-error">
              {standing.error}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-4">
            <div className="h-20 w-20 overflow-hidden rounded-full border border-white/10 bg-black/40">
              {photo
                ? <img src={photo} alt="WhatsApp display photo" data-testid="wa-profile-photo" className="h-full w-full object-cover" />
                : <div className="flex h-full w-full items-center justify-center text-slate-600"><Camera size={22} /></div>}
            </div>
            <label
              data-testid="wa-profile-photo-upload"
              className="cursor-pointer rounded-full border border-fuchsia-400/40 bg-fuchsia-400/10 px-4 py-2 text-xs font-bold text-fuchsia-200 hover:bg-fuchsia-400/20"
            >
              {busy ? "Working…" : photo ? "Change display photo" : "Upload display photo"}
              <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            </label>
            <span className="text-[11px] text-slate-500">Your logo works best — it is cropped to a circle, so keep it centred.</span>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-xs" data-testid="wa-profile-standing">
            <p className="flex items-center gap-2 text-slate-300">
              {nameOk ? <BadgeCheck size={15} className="text-green-400" /> : <Clock size={15} className="text-yellow-400" />}
              Display name <span className="font-bold">{standing?.display_name || "—"}</span>
              {nameOk ? "is approved by Meta" : `is ${(standing?.name_status || "pending").toLowerCase().replace(/_/g, " ")}`}
            </p>
            <p className="mt-2 flex items-center gap-2 text-slate-300">
              {badge ? <BadgeCheck size={15} className="text-cyan-300" /> : <Clock size={15} className="text-slate-500" />}
              {badge
                ? "Green tick (Official Business Account) is active on your number"
                : "Green tick not granted yet — Meta gives it only to well-known brands, and only Meta can approve it"}
            </p>
            {!badge && (
              <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                What actually helps: get the display name approved, complete business verification in Meta Business Settings,
                keep your message quality green, and apply from WhatsApp Manager → Account tools → Official Business Account.
                A display photo plus this profile already removes most of the "is this fake?" doubt.
              </p>
            )}
            <p className="mt-2 text-[11px] text-slate-500">
              Quality {standing?.quality || "—"} · you can message{" "}
              {String(standing?.limit_tier || "").replace("TIER_", "").replace("K", ",000").replace("UNLIMITED", "unlimited") || "—"}{" "}
              customers every 24 hours
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">About (shown under your name)</label>
              <input data-testid="wa-profile-about" value={form.about} maxLength={139} onChange={(e) => setForm({ ...form, about: e.target.value })} placeholder="Sublimation blanks at wholesale price" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Website</label>
              <input data-testid="wa-profile-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://sublimationwala.in" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</label>
              <input data-testid="wa-profile-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="varshasublimation@gmail.com" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Address</label>
              <input data-testid="wa-profile-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Shop address, city" className={`${inputCls} mt-1`} />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">What you sell</label>
              <textarea data-testid="wa-profile-description" rows={2} value={form.description} maxLength={512} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Wholesale sublimation blanks — mugs, T-shirts, frames, gift items. Pan-India delivery." className={`${inputCls} mt-1`} />
            </div>
          </div>

          <button
            data-testid="wa-profile-save"
            onClick={save}
            disabled={busy}
            className="rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-6 py-2.5 text-sm font-bold text-[#0A0B0E] disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save profile"}
          </button>
        </div>
      )}
    </div>
  );
}
